import { useMutation, useQuery } from "@apollo/client";
import { useFormik } from "formik";
import { Link, useParams } from "react-router-dom";
import { useState } from "react";
import { Table, multiSelect } from "../../../../../../components/Functional";
import { Button, Modal } from "../../../../../../components/ui";
import { UPDATE_WAREHOUSE_MOVE as updateWarehouseMoveMutation } from "../../../../../../graphql/mutation/WarehouseMove";
import {
  REMOVE_SHIPMENT_WAREHOUSE_ITEMS_BY_IDS as removeShipmentWarehouseItemsByIdsMutation,
} from "../../../../../../graphql/mutation/Shipment";
import { FETCH_WAREHOUSE_MOVE_V2 as fetchWarehouseMoveV2 } from "../../../../../../graphql/query/WarehouseMove";
import { FETCH_WAREHOUSE_LOCATIONS } from "../../../../../../graphql/query/WarehouseLocation";
import { FETCH_ARTWORK_TARIFFS } from "../../../../../../graphql/query/ArtworkTariff";
import { getInput } from "../../../../../../components/ui";

import AddItem from "./components/AddItem";
import Actions from "./components/Actions";
import SectionHeader from "../../../../../Warehouse/pages/Wizard/components/SectionHeader";
import AddWork from "./components/AddWork";

const EditionNumber = ({ label }) => {
  const { edition, editionNumber } = label || {};
  if (editionNumber && edition?.number && edition?.number > 1) {
    return (
      <div className="flex flex-col">
        <span className="text-base font-medium leading-5 text-black">{`${editionNumber}/${edition?.number}`}</span>
      </div>
    );
  }
};

const TitleAndArtists = ({ values }) => {
  const title = values?.item?.instance?.edition?.artwork?.title;
  const artists =
    values?.item?.instance?.edition?.artwork?.artists
      ?.map(artist => artist?.name)
      .filter(Boolean)
      .join(", ") || "";
  const description = values?.item?.description;
  return (
    <div
      className="break-words block w-full min-h-full"
      title={description}
    >
      {title ? (
        <div className="font-medium text-black break-words">{title}</div>
      ) : null}
      {artists ? (
        <div className="mt-1 text-xs text-black/60 break-words">{artists}</div>
      ) : null}
    </div>
  );
};

const Items = props => {
  const { shipment } = props || {};
  const { id: idParam } = useParams() || {};
  const shipmentId = shipment?.id ?? (idParam != null && idParam !== "" ? +idParam : undefined);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      limit: 50,
      offset: 0,
      _shipment: {
        id: { operator: "eq", value: shipmentId },
      },
      selected: { ids: {}, checked: false, select: null },
    },
  });

  const selectedIds = Object.values(formik?.values?.selected?.ids || {})
    .map(row => row?.id)
    .filter(id => id != null);
  const hasSelection = selectedIds.length > 0;
  const clearSelected = () =>
    formik.setFieldValue("selected", {
      ids: {},
      checked: false,
      select: null,
    });

  const [failedRemoveItems, setFailedRemoveItems] = useState([]);

  const [
    removeShipmentWarehouseItemsByIds,
    {
      data: {
        removeShipmentWarehouseItemsByIds: {
          error: removeItemsError,
          success: removeItemsSuccess,
        } = {},
      } = {},
      loading: removeItemsLoading,
      reset: resetRemove,
    },
  ] = useMutation(removeShipmentWarehouseItemsByIdsMutation(), {
    refetchQueries: ["FetchWarehouseMoveV2"],
  });

  const removeSelectedModalProps = {
    title: removeItemsError ? "Error" : "Remove selected items",
    scale: "sm",
    description: removeItemsError
      ? removeItemsError
      : `Remove ${selectedIds.length} item(s) from this shipment?`,
    closeOnBackdrop: true,
    body: ({ closeModal }) => (
      <div className="mt-4 flex">
        <Button
          className="mr-3"
          label={removeItemsError ? "Close" : "Cancel"}
          action={removeItemsError ? "primary" : "default"}
          onClick={() => {
            resetRemove();
            closeModal?.();
          }}
        />
        {!removeItemsError && (
          <Button
            label={removeItemsLoading ? "Removing…" : "Remove"}
            disabled={removeItemsLoading}
            onClick={() => {
              removeShipmentWarehouseItemsByIds({
                variables: {
                  input: { shipmentId, warehouseMoveIds: selectedIds },
                },
                update: async (_, { data }) => {
                  const result = data?.removeShipmentWarehouseItemsByIds;
                  const failed = result?.failedRemoveItems ?? [];
                  const failedMoveIds = failed
                    .map(item => item?.moveId)
                    .filter(moveId => moveId != null);
                  const selectedEntries = Object.entries(
                    formik?.values?.selected?.ids || {},
                  );
                  const remainingSelectedEntries = selectedEntries.filter(
                    ([, row]) => failedMoveIds.includes(row?.id),
                  );
                  const remainingSelectedIds = remainingSelectedEntries.reduce(
                    (accumulator, [key, row]) => ({
                      ...accumulator,
                      [key]: row,
                    }),
                    {},
                  );

                  setFailedRemoveItems(failed);
                  if (failedMoveIds.length > 0) {
                    formik.setFieldValue("selected", {
                      ids: remainingSelectedIds,
                      checked: false,
                      select: null,
                    });
                  } else {
                    clearSelected();
                  }
                  resetRemove();
                  await closeModal?.();
                },
              });
            }}
          />
        )}
      </div>
    ),
  };

  const { data: { artworkTariffs = [] } = {} } = useQuery(
    FETCH_ARTWORK_TARIFFS,
  );
  const { data: { warehouseLocations = [] } = {} } = useQuery(
    FETCH_WAREHOUSE_LOCATIONS,
  );

  const multiSelectProps = {
    formik,
    label: "Items",
    name: "selected",
    keyName: "id",
  };

  const tableProps = {
    className: "sm:px-0 lg:px-0",
    dataKey: "warehouseMoveV2",
    formik,
    FETCH_QUERY: fetchWarehouseMoveV2({
      // WarehouseMove scalar fields
      confirmed: true,
      ti: true,
      value: true,
      bondref: true,
      taDays: true,
      siteId: true,
      goodsCode: true,
      user: true,
      shipmentId: true,
      // WarehouseMove associations
      site: true,
      locationId: true,
      item: {
        description: true,
        category: true,
        instance: {
          name: true,
          editionNumber: true,
          edition: {
            number: true,
            year: true,
            artwork: {
              title: true,
              artists: {
                name: true,
              },
            },
          },
        },
      },
    }),
    headers: [
      { ...multiSelect(multiSelectProps), width: "30px" },
      {
        label: "Item #",
        name: "item.id",
        sortKey: "id",
        type: "custom",
        width: "65px",
        component: ({ values }) => (
          <Link
            className={`underline ${!values?.confirmed ? "!text-[#FF7400]" : values?.ti === -1 ? "!text-[#8400FF]" : ""}`}
            to={`/warehouse/${values?.item?.id}/details`}
          >
            {values?.item?.id}
          </Link>
        ),
      },
      {
        label: "Category",
        name: "item.category.description",
        type: "label",
        width: "90px",
      },
      {
        label: "Title / Artist",
        name: "item",
        type: "custom",
        component: TitleAndArtists,
        minWidth: "225px",
        wrap: true,
      },
      {
        label: "Reference",
        name: "item.instance.name",
        type: "custom",
        width: "75px",
        component: ({ values }) => (
          <Link
            className="underline"
            to={`/instance/${values?.item?.instance?.id}/details`}
          >
            {values?.item?.instance?.name}
          </Link>
        ),
      },
      {
        label: "Edition",
        name: "item.instance",
        type: "custom",
        component: EditionNumber,
        width: "36px",
      },
      {
        label: "Year",
        name: "item.instance.edition.year",
        type: "label",
        width: "52px",
      },
      {
        inputType: "number-currency",
        label: "Value",
        name: "value",
        type: "input",
        allowEmpty: true,
        width: "100px",
      },
      {
        designType: "threeStateCheckbox",
        inputType: "toggle",
        label: "CC",
        name: "ti",
        type: "input",
        width: "42px",
      },
      {
        inputType: "text",
        label: "Ref",
        name: "bondref",
        type: "input",
        width: "75px",
      },
      {
        inputType: "number-currency",
        label: "TA Days",
        name: "taDays",
        type: "input",
        inputClassName: "w-[65px]",
        allowDecimal: false,
        allowEmpty: true,
        width: "58px",
      },
      {
        label: "Location",
        name: "locationId",
        type: "custom",
        width: "160px",
        inputClassName: "w-full min-w-0",
        component: props => {
          const { values } = props || {};

          const locations =
            warehouseLocations
              ?.filter(item => item?.id === 0 || item?.siteId == values?.siteId)
              ?.map(item => ({
                label: item?.description,
                value: item.id,
              })) || [];

          const input = getInput({
            ...props,
            label: "",
            type: "multi-select",
            options: locations,
          });

          return input;
        },
      },
      {
        type: "input",
        label: "Goods Code",
        name: "goodsCode",
        width: "90px",
        inputType: "multi-select",
        options: artworkTariffs?.map(tariff => ({
          label: tariff?.code,
          value: tariff?.code,
        })),
      },
      {
        label: "User",
        name: "user",
        type: "label",
        width: "75px",
      },
      {
        label: "",
        name: "action",
        type: "custom",
        component: Actions,
        width: "60px",
      },
    ],
    UPDATE_MUTATION: updateWarehouseMoveMutation({
      // Return edited scalar fields so Apollo cache doesn't revert.
      bondref: true,
      goodsCode: true,
      locationId: true,
      siteId: true,
      taDays: true,
      ti: true,
      value: true,
      item: { category: {}, instance: { edition: { artwork: {} } } },
      site: true,
    }),
    variables: {
      _shipment: {
        id: {
          operator: "eq",
          value: shipmentId,
        },
      },
    },
    skip: () => !shipmentId,
    variant: "dynamicHeight",
    allowColumnWrap: true,
  };

  const actions = [
    ...(hasSelection && shipmentId != null
      ? [
        {
          component: () => (
            <Modal {...removeSelectedModalProps}>
              <Button
                label={`Remove selected (${selectedIds.length})`}
                variant="default"
              />
            </Modal>
          ),
        },
      ]
      : []),
    {
      component: () => (
        <>
          <AddItem shipment={shipment ?? { id: shipmentId }} />
          <AddWork shipment={shipment ?? { id: shipmentId }} />
        </>
      ),
    },
  ];

  const failedRemoveModalProps = {
    scale: "md",
    hideCloseButton: true,
    closeOnBackdrop: true,
    isOpen: failedRemoveItems.length > 0,
    onClose: () => setFailedRemoveItems([]),
    body: ({ closeModal }) => (
      <div className="mt-6 flex w-full flex-col">
        <div className="flex flex-col">
          <div className="text-2xl font-bold">Some items could not be removed</div>
          <div className="mt-6">
            {failedRemoveItems.length} item(s) could not be removed from the
            shipment:
          </div>
          <div className="mt-4 max-h-64 overflow-y-auto rounded bg-[#2222220D] p-3 text-sm text-black">
            <ul className="flex list-disc flex-col gap-1 pl-5">
              {failedRemoveItems.map((item, index) => (
                <li key={item?.moveId ?? index}>
                  {item?.warehouseItemId != null ? (
                    <>
                      Item:{" "}
                      <Link
                        className="underline"
                        to={`/warehouse/${item.warehouseItemId}/movements`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        #{item.warehouseItemId}
                      </Link>
                    </>
                  ) : (
                    <span>Warehouse item</span>
                  )}
                  {item?.subsequentShipmentId != null ? (
                    <>
                      {" "}
                      (
                      <Link
                        className="underline"
                        to={`/shipments/${item.subsequentShipmentId}/items`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Shipment #{item.subsequentShipmentId}
                      </Link>
                      )
                    </>
                  ) : null}
                  {item?.message ? (
                    <span className="ml-1"> — {item.message}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-8 flex flex-row">
          <div className="flex flex-col">
            <div>
              <Button
                className="mr-4"
                label="Close"
                onClick={() => {
                  setFailedRemoveItems([]);
                  closeModal?.();
                }}
              />
            </div>
          </div>
        </div>
      </div>
    ),
  };

  return (
    <div className="w-full">
      <Modal {...failedRemoveModalProps} />
      <SectionHeader
        title={`Items - Shipment ${shipmentId ?? "…"}`}
        actions={actions}
      />
      <div className="w-96 px-8 pb-2">
        <p className="mb-2">Unconfirmed Items</p>
        <p className="bg-[#2222220D] p-3 text-red-500">
          {shipment?.unconfirmedItemCount ?? "—"}
        </p>
      </div>
      <div className="mb-6 w-full">
        <Table {...tableProps} />
      </div>
    </div>
  );
};

export default Items;
